/* eslint-disable react-hooks/exhaustive-deps */
import React, { useContext, useEffect, useState } from "react";
import { makeStyles } from "@material-ui/core/styles";
import AppContext from "AppContext";
import { useDispatch, useSelector } from "react-redux";
import Page from "components/common_components/Page/Page";
import { notifyError, notifySuccess, notifyWarning } from "redux/actions/Notifications";
import { Button, LinearProgress } from "@material-ui/core";
import Paper from "@material-ui/core/Paper";
import RefreshIcon from "@material-ui/icons/Refresh";
import EmailIcon from "@material-ui/icons/Email";
import PhoneIcon from "@material-ui/icons/PhoneIphone";
import EnhancedTable from "components/common_components/Tables/EnhancedTable";
import { useHistory } from "react-router";
import IconButton from "@material-ui/core/IconButton";
import Tooltip from "@material-ui/core/Tooltip";
import CloudDoneIcon from "@material-ui/icons/CloudDone";
import { hasElements } from "utils/arrays";
import { Tenant } from "utils/tenants";

const useStyles = makeStyles((theme) => ({
    root: {
        display: "flex",
        flexDirection: "column",
        width: "100%",
    },
    header: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        padding: "0px 30px",
        fontSize: "large",
        marginBottom: 20,
        marginTop: 30,
    },
    title: {
        fontSize: "large",
    },
    linearProgress: {
        width: 50,
        marginLeft: 5,
    },
}));

const DEFAULT_SMS_BODY =
    "Hola, nos estamos poniendo en contacto con usted desde la Oficina Legal de Diller V. Paz.\n\n" +
    "Lo estamos contactando porque nuestro sistema muestra que no ha realizado un pago en más de 90 días. Por favor, póngase en contacto con la oficina para realizar un pago al 281-401-9609.\n\n" +
    `${Tenant.getName()}.`;

const defaultContactTypes = {
    P_EML: "-1",
    P_SMS: "-2",
};

function PendingPayments(props) {
    const classes = useStyles();
    const [pendingPayments, setPendingPayments] = useState(undefined);
    const [emailSentIds, setEmailSentIds] = useState([]);
    const [emailSendingIds, setEmailSendingIds] = useState([]);
    const [smsSentIds, setSmsSentIds] = useState([]);
    const [smsSendingIds, setSmsSendingIds] = useState([]);
    const dispatch = useDispatch();
    const history = useHistory();
    const { user } = useSelector((state) => state.Login);

    const {
        api: {
            Modules,
            General: { PendingPayments },
        },
    } = useContext(AppContext);

    function registerProgressInfo(module, fileCaseId, contactTypeId) {
        return Modules(module).ProgressInfo.addAsync({
            fileCaseId,
            date: new Date().getTime(),
            contactTypeId,
            isImportant: false,
            comments: DEFAULT_SMS_BODY,
            userId: user.id,
        });
    }

    async function registerProgressInfoSingle(module, fileCaseId, contactTypeId) {
        try {
            const response = await registerProgressInfo(module, fileCaseId, contactTypeId);
            if (response && response.status === 200) {
                dispatch(notifySuccess("Progress Information saved successfully!"));
            }
        } catch (error) {
            dispatch(notifyError("Unable to register progress information!"));
            console.error("Unable to register progress information!", error);
        }
    }

    async function registerProgressInfoBulk(items, contactTypeId) {
        try {
            const pp_promises = items.map(({ fileCaseId, module }) => {
                return registerProgressInfo(module, fileCaseId, contactTypeId);
            });

            const pp_responses = await Promise.all(pp_promises);
            if (hasElements(pp_responses)) {
                dispatch(notifySuccess("Progress Information saved successfully!"));
            }
        } catch (error) {
            dispatch(notifyError("Unable to register progress information!"));
            console.error("Unable to register progress information!", error);
        }
    }

    async function getPendingPayments() {
        const res = await PendingPayments.getAllAsync();
        setPendingPayments(res);
    }

    useEffect(() => {
        getPendingPayments();

        return () => {
            setPendingPayments([]);
        };
    }, []);

    function refresh() {
        getPendingPayments();
    }

    async function onSendEmailClickHandler(event, item) {
        event.preventDefault();
        event.stopPropagation();
        // Send email
        try {
            setEmailSendingIds((prev) => [...prev, item.id]);
            const res = await PendingPayments.sendEmailAsync({ payload: { clientId: item.clientId } });
            if (res) {
                setEmailSentIds((prev) => [...prev, item.id]);
                dispatch(notifySuccess("Email sent!"));

                // Register progress information
                await registerProgressInfoSingle(item.module, item.fileCaseId, defaultContactTypes.P_EML);
            } else {
                dispatch(notifyError("Email not sent. Ask an administrator for more information."));
            }
        } catch (error) {
            dispatch(notifyError("Email not sent. \n" + error));
        } finally {
            setEmailSendingIds((prev) => prev.filter((x) => x !== item.id));
        }
    }

    async function onSendSMSClickHandler(event, item) {
        event.preventDefault();
        event.stopPropagation();
        // Send SMS
        try {
            setSmsSendingIds((prev) => [...prev, item.id]);
            const res = await PendingPayments.sendSMSAsync({ payload: { clientId: item.clientId, body: DEFAULT_SMS_BODY } });
            if (res) {
                setSmsSentIds((prev) => [...prev, item.id]);
                dispatch(notifySuccess("SMS sent!"));

                // Register progress information
                await registerProgressInfoSingle(item.module, item.fileCaseId, defaultContactTypes.P_SMS);
            } else {
                dispatch(notifyError("SMS not sent. Ask an administrator for more information."));
            }
        } catch (error) {
            dispatch(notifyError("SMS not sent. \n" + error));
        } finally {
            setSmsSendingIds((prev) => prev.filter((x) => x !== item.id));
        }
    }

    const headCells = [
        {
            index: 1,
            id: "fileNo",
            disablePadding: false,
            label: "File No.",
            render: (_, { value, data }) => {
                return (
                    <a
                        href="#top"
                        onClick={() => {
                            const { module, fileCaseId } = data;
                            if (module && fileCaseId) {
                                history.push(`/${module}?fileCaseId=${fileCaseId}`);
                            }
                        }}
                    >
                        {value}
                    </a>
                );
            },
        },
        { index: 2, id: "clientFullName", disablePadding: false, label: "Client" },
        {
            index: 3,
            id: "clientEmail",
            disablePadding: false,
            label: "Email",
            render: (_, { value, data }) => {
                return (
                    <a
                        href={`https://mail.google.com/mail/?view=cm&fs=1&to=${String(value).trim().toLowerCase()}`}
                        target="_blank"
                        rel="noopener noreferrer"
                    >
                        {value}
                    </a>
                );
            },
        },
        { index: 4, id: "lastPaymentDateString", disablePadding: false, label: "Last Payment" },
        { index: 5, id: "daysWithoutPaying", disablePadding: false, label: "Days Count" },
        { index: 6, id: "balance", disablePadding: false, label: "Balance", type: "currency" },
        { index: 7, id: "concept", disablePadding: false, label: "Concept" },
        {
            index: 8,
            id: "actions",
            disablePadding: false,
            label: "Actions",
            render: (_, { value, data }) => {
                if (!data.clientEmail) {
                    return null;
                }
                if (emailSendingIds.includes(data.id) || smsSendingIds.includes(data.id)) {
                    return <LinearProgress className={classes.linearProgress} />;
                }
                if (emailSentIds.includes(data.id) || smsSentIds.includes(data.id)) {
                    return (
                        <Button variant="text" disabled size="small" color="primary" endIcon={<CloudDoneIcon />}>
                            Sent!
                        </Button>
                    );
                }
                return (
                    <>
                        <Button
                            variant="text"
                            size="small"
                            onClick={(e) => onSendEmailClickHandler(e, data)}
                            color="default"
                            startIcon={<EmailIcon color="primary" />}
                        >
                            Email
                        </Button>
                        <Button
                            variant="text"
                            size="small"
                            onClick={(e) => onSendSMSClickHandler(e, data)}
                            color="default"
                            startIcon={<PhoneIcon color="primary" />}
                        >
                            SMS
                        </Button>
                    </>
                );
            },
        },
    ];

    async function onSendEmailsBulkClickHandler(selectedItems, clearSelection) {
        // Send emails
        const promises = selectedItems.map((x) => PendingPayments.sendEmailAsync({ payload: { clientId: x.clientId, id: x.id } }));

        try {
            setEmailSendingIds(selectedItems.map((x) => x.id));
            const responses = await Promise.all(promises);

            if (hasElements(responses)) {
                const successfullResponses = responses.filter((x) => x.data === true);
                const newEmailSentIds = successfullResponses.map((x) => JSON.parse(x.config.data).id);
                setEmailSentIds((prev) => [...prev, ...newEmailSentIds]);
                if (successfullResponses.length === responses.length) {
                    dispatch(notifySuccess("Emails sent!"));
                } else {
                    dispatch(notifyWarning("Some emails weren't sent!"));
                }

                // Register progress information Bulk
                const items = selectedItems.filter((x) => newEmailSentIds.includes(x.id));
                await registerProgressInfoBulk(items, defaultContactTypes.P_EML);
            }
        } catch (error) {
            dispatch(notifyError("Emails not sent. \n" + error));
        } finally {
            setEmailSendingIds([]);
            clearSelection();
        }
    }

    async function onSendSMSBulkClickHandler(selectedItems, clearSelection) {
        // Send SMSs
        const promises = selectedItems.map((x) =>
            PendingPayments.sendSMSAsync({ payload: { clientId: x.clientId, body: DEFAULT_SMS_BODY, id: x.id } })
        );

        try {
            setSmsSendingIds(selectedItems.map((x) => x.id));
            const responses = await Promise.all(promises);

            if (hasElements(responses)) {
                const successfullResponses = responses.filter((x) => x.data === true);
                const newSmsSentIds = successfullResponses.map((x) => JSON.parse(x.config.data).id);
                setSmsSentIds((prev) => [...prev, ...newSmsSentIds]);
                if (successfullResponses.length === responses.length) {
                    dispatch(notifySuccess("SMSs sent!"));
                } else {
                    dispatch(notifyWarning("Some SMSs weren't sent!"));
                }

                // Register progress information Bulk
                const items = selectedItems.filter((x) => newSmsSentIds.includes(x.id));
                await registerProgressInfoBulk(items, defaultContactTypes.P_SMS);
            }
        } catch (error) {
            dispatch(notifyError("SMSs not sent. \n" + error));
        } finally {
            setSmsSendingIds([]);
            clearSelection();
        }
    }

    function renderBulkActions(selectedItemIds, clearSelection) {
        const selectedItems = pendingPayments
            .filter((x) => selectedItemIds.includes(x.id))
            .map(({ id, clientId, module, fileCaseId }) => ({ id, clientId, module, fileCaseId }));
        return (
            <>
                <Tooltip title="Send emails" onClick={() => onSendEmailsBulkClickHandler(selectedItems, clearSelection)}>
                    <IconButton aria-label="send-emails">
                        <EmailIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Send SMS messages" onClick={() => onSendSMSBulkClickHandler(selectedItems, clearSelection)}>
                    <IconButton aria-label="send-emails">
                        <PhoneIcon color="primary" />
                    </IconButton>
                </Tooltip>
            </>
        );
    }

    return (
        <Page className={classes.root}>
            <Paper>
                <div className={classes.header}>
                    <div className={classes.title}>Pending Payments</div>
                    <Button onClick={refresh} variant="outlined" size="small" color="primary" startIcon={<RefreshIcon />}>
                        Refresh
                    </Button>
                </div>
                {emailSentIds && pendingPayments && Array.isArray(pendingPayments) && (
                    <EnhancedTable
                        headCells={headCells}
                        data={pendingPayments}
                        keyColumnName="id"
                        defaultOrder={{ order: "asc", orderBy: "ordinal" }}
                        selectable={true}
                        deletable={false}
                        filterable={false}
                        pageable={false}
                        dense
                        renderBulkActions={renderBulkActions}
                    />
                )}
            </Paper>
        </Page>
    );
}

export default PendingPayments;
